import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'react-bootstrap'
import { set } from 'lodash-es'
import { OrderFields } from 'types/order'
import { selectOrderWrite, selectOrderFormErrors } from 'store/order/order.selectors'
import { changeOrderHandler } from 'store/order/order.actions'
import { selectCurrentUser } from 'store/users/users.selectors'
import { getDateForServer, getDateTimeForServer } from 'utils/date'
import InputControl from 'components/molecules/InputControl'
import InputDate from 'components/molecules/InputDate'
import InputDateTime from 'components/molecules/InputDateTime'
import Title from 'components/atoms/Title'
import { RouteProps } from './index'

const Customer: React.FC<RouteProps> = ({ onChange }) => {
  const dispatchStore = useDispatch()
  const form = useSelector(selectOrderWrite)
  const user = useSelector(selectCurrentUser)
  const errors = useSelector(selectOrderFormErrors)

  const routeDateStartSetToday = useCallback(() => {
    dispatchStore(changeOrderHandler({ [OrderFields.ROUTE_DATE_START]: getDateForServer(new Date()) }))
  }, [dispatchStore])

  const routeDateStartSetTomorrow = useCallback(() => {
    const today = new Date()
    const tomorrow = new Date()
    tomorrow.setDate(today.getDate() + 1)
    dispatchStore(changeOrderHandler({ [OrderFields.ROUTE_DATE_START]: getDateForServer(tomorrow) }))
  }, [dispatchStore])

  const setDayTime = useCallback(() => {
    const now = form[OrderFields.ROUTE_DATE_START] ? new Date(form[OrderFields.ROUTE_DATE_START]) : new Date()
    const startOfDay = new Date(now)
    startOfDay.setHours(7, 0, 0, 0)

    const endOfDay = new Date(now)
    endOfDay.setHours(20, 0, 0, 0)

    const changed = {}
    set(changed, 'meta.kppExpectedDateFrom', getDateTimeForServer(startOfDay))
    set(changed, 'meta.kppExpectedDateTo', getDateTimeForServer(endOfDay))

    dispatchStore(changeOrderHandler(changed))
  }, [dispatchStore, form])

  const setNightTime = useCallback(() => {
    const now = form[OrderFields.ROUTE_DATE_START] ? new Date(form[OrderFields.ROUTE_DATE_START]) : new Date()
    const startOfNight = new Date(now)
    startOfNight.setHours(19, 0, 0, 0)

    const endOfNight = new Date(now)
    endOfNight.setDate(now.getDate() + 1)
    endOfNight.setHours(8, 0, 0, 0)

    const changed = {}
    set(changed, 'meta.kppExpectedDateFrom', getDateTimeForServer(startOfNight))
    set(changed, 'meta.kppExpectedDateTo', getDateTimeForServer(endOfNight))

    dispatchStore(changeOrderHandler(changed))
  }, [dispatchStore, form])

  return (
    <>
      <Title text="Рейс и маршрут" />
      <ul className="list-group needs-validation">
        <li className="list-group-item row">
          <div className="p-0">
            <InputDate
              id={OrderFields.ROUTE_DATE_START}
              value={form[OrderFields.ROUTE_DATE_START]}
              isValid={errors[OrderFields.ROUTE_DATE_START]?.isValid}
              invalidMessage={errors[OrderFields.ROUTE_DATE_START]?.invalidMessage}
              onChangeHandler={onChange}
              className="col-lg-4 col-md-6 col-sm-12"
              label="Дата начала:"
            />
          </div>
          <div className="col-lg-4 col-md-6 col-sm-12 p-0 d-flex" style={{ marginTop: '12px' }}>
            <Button className="p-0" style={{ flex: '1', marginRight: '4px' }} onClick={routeDateStartSetToday}>
              Сегодня
            </Button>
            <Button className="p-0" style={{ flex: '1', marginLeft: '4px' }} onClick={routeDateStartSetTomorrow}>
              Завтра
            </Button>
          </div>
        </li>
        {user!.features?.kppSync ? (
          <li className="list-group-item row">
            <InputDateTime
              id="meta.kppExpectedDateFrom"
              value={(form[OrderFields.META].kppExpectedDateFrom as string) || ''}
              isValid={errors[OrderFields.META].kppExpectedDateFrom?.isValid}
              invalidMessage={errors[OrderFields.META].kppExpectedDateFrom?.invalidMessage}
              onChangeHandler={onChange}
              className="col-lg-4 col-md-6 col-sm-12"
              label="Начало периода ожидаемой даты:"
            />
            <InputDateTime
              id="meta.kppExpectedDateTo"
              value={(form[OrderFields.META].kppExpectedDateTo as string) || ''}
              isValid={errors[OrderFields.META].kppExpectedDateTo?.isValid}
              invalidMessage={errors[OrderFields.META].kppExpectedDateTo?.invalidMessage}
              onChangeHandler={onChange}
              className="col-lg-4 col-md-6 col-sm-12"
              label="Конец периода ожидаемой даты:"
            />
            <div className="col-lg-4 col-md-6 col-sm-12 p-0 d-flex" style={{ marginTop: '12px' }}>
              <Button className="p-0" style={{ flex: '1', marginRight: '4px' }} onClick={setDayTime}>
                День
              </Button>
              <Button className="p-0" style={{ flex: '1', marginLeft: '4px' }} onClick={setNightTime}>
                Ночь
              </Button>
            </div>
          </li>
        ) : null}
        <li className="list-group-item row">
          <div className="p-0">
            <InputControl
              type="text"
              id={OrderFields.SHIPPER}
              value={form[OrderFields.SHIPPER]}
              isValid={errors[OrderFields.SHIPPER]?.isValid}
              invalidMessage={errors[OrderFields.SHIPPER]?.invalidMessage}
              onChangeHandler={onChange}
              className="col-lg-4 col-md-6 col-sm-12"
              label="Грузоотправитель / Заказчик:"
            />
          </div>
        </li>
        <li className="list-group-item row">
          <div className="p-0">
            <InputControl
              type="text"
              id={OrderFields.ROUTE_TRIP}
              value={form[OrderFields.ROUTE_TRIP]}
              isValid={errors[OrderFields.ROUTE_TRIP]?.isValid}
              invalidMessage={errors[OrderFields.ROUTE_TRIP]?.invalidMessage}
              onChangeHandler={onChange}
              className="col-lg-4 col-md-6 col-sm-12"
              label="Маршрут:"
            />
          </div>
        </li>
        <li className="list-group-item row">
          <div className="p-0">
            <InputControl
              type="text"
              id={OrderFields.CARGO}
              value={form[OrderFields.CARGO]}
              isValid={errors[OrderFields.CARGO]?.isValid}
              invalidMessage={errors[OrderFields.CARGO]?.invalidMessage}
              onChangeHandler={onChange}
              className="col-lg-4 col-md-6 col-sm-12"
              label="Груз:"
            />
          </div>
        </li>
      </ul>
    </>
  )
}

export default Customer
